// src/components/configuration/sections/ServicesSectionShadcn.jsx - Pure shadcn implementation
import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  Plus,
  Wrench
} from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Spinner } from '../../ui/spinner';

// Keep your existing PageContainer for headers
import PageContainer from '../../ui/layout/PageContainer/PageContainer';

// Import the shadcn split panel components
import ServicesListShadcn from '../../ui/sections/ServicesList/ServicesListShadcn';
import ServiceEditorShadcn from '../../ui/sections/ServiceEditor/ServiceEditorShadcn';

// Keep your existing hook - same data pipeline!
import { useSettings } from '../../../hooks/useSettings';

const ServicesSectionShadcn = () => {
  // Get real data from settings context - EXACT same hook as before
  const {
    services,
    createService,
    updateService,
    deleteService,
    createServiceComponent,
    updateServiceComponent,
    deleteServiceComponent,
    loading,
    savingServices,
    savingComponents,
    error,
    getActiveServices,
    getServiceById,
    getServiceComponents,
    isConnected
  } = useSettings();

  // Local UI state for split panel - same as before
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Auto-select first service when services load - same as before
  useEffect(() => {
    if (services && services.length > 0 && !selectedServiceId) {
      setSelectedServiceId(services[0].id);
    }
  }, [services, selectedServiceId]);

  // Filter services based on search - same logic as before
  const filteredServices = services?.filter(service => {
    const matchesSearch = !searchTerm ||
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  }) || [];

  // Get selected service object - same as before
  const selectedService = selectedServiceId ? getServiceById(selectedServiceId) : null;

  // Handle service operations - same logic as before!
  const handleCreateService = (serviceData) => {
    console.log('🔧 Creating new service:', serviceData);
    createService(serviceData);
    // Success/error handling is managed by the provider
  };

  const handleUpdateService = (serviceId, updateData) => {
    console.log('🔧 Updating service:', serviceId, updateData);
    updateService(serviceId, updateData);
    // Success/error handling is managed by the provider
  };

  const handleDeleteService = (service) => {
    if (window.confirm(`Are you sure you want to delete "${service.name}"? This will also delete all its components.`)) {
      console.log('🗑️ Deleting service:', service.id);
      deleteService(service.id);

      // If deleted service was selected, clear selection
      if (selectedServiceId === service.id) {
        setSelectedServiceId(null);
      }
      // Success/error handling is managed by the provider
    }
  };

  // Handle component operations - same logic as before!
  const handleCreateComponent = (serviceId, componentData) => {
    console.log('🧩 Creating component for service:', serviceId, componentData);
    createServiceComponent(serviceId, componentData);
    // Success/error handling is managed by the provider
  };

  const handleUpdateComponent = (serviceId, componentId, updateData) => {
    console.log('🧩 Updating component:', componentId, updateData);
    updateServiceComponent(serviceId, componentId, updateData);
    // Success/error handling is managed by the provider
  };

  const handleDeleteComponent = (serviceId, componentId, componentName) => {
    if (window.confirm(`Are you sure you want to delete "${componentName}"?`)) {
      console.log('🗑️ Deleting component:', componentId);
      deleteServiceComponent(serviceId, componentId);
      // Success/error handling is managed by the provider
    }
  };

  // Loading state - now using shadcn styling
  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading services...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving your service offerings</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport">
      {/* Error Message - Provider handles success/error states */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Panel Layout */}
      <div className="grid grid-cols-12 gap-4 h-full">
        {/* Left Panel - Services List (33% width) */}
        <div className="col-span-4 h-full min-h-0">
          <ServicesListShadcn
            services={filteredServices}
            selectedServiceId={selectedServiceId}
            onSelectService={setSelectedServiceId}
            onCreateService={handleCreateService}
            onDeleteService={handleDeleteService}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            loading={savingServices}
          />
        </div>

        {/* Right Panel - Service Editor (67% width) */}
        <div className="col-span-8 h-full min-h-0">
          <ServiceEditorShadcn
            service={selectedService}
            serviceComponents={selectedService ? getServiceComponents(selectedService.id) : []}
            onUpdateService={handleUpdateService}
            onCreateComponent={handleCreateComponent}
            onUpdateComponent={handleUpdateComponent}
            onDeleteComponent={handleDeleteComponent}
            savingService={savingServices}
            savingComponents={savingComponents}
          />
        </div>
      </div>

      {/* Empty State - when no services exist */}
      {(!services || services.length === 0) && !loading && (
        <div className="text-center py-16">
          <Wrench className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No Services Created</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Get started by creating your first service offering. Define what you provide, set pricing, and add components.
          </p>
          <Button
            size="lg"
            onClick={() => {
              // This will trigger the ServicesList add service flow
              console.log('Create first service');
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Your First Service
          </Button>
        </div>
      )}
    </PageContainer>
  );
};

export default ServicesSectionShadcn;